import React from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import HUDFrame from './ui/HUDFrame';
import { BiologicalSex } from '../types';
import ArmyLogo from './united-states-army-2023-seeklogo.png';

interface BiodataEntryProps {
  onSelect: (sex: BiologicalSex) => void;
  onBack?: () => void;
  onClickSound?: () => void;
}

const BiodataEntry: React.FC<BiodataEntryProps> = ({ onSelect, onBack, onClickSound }) => {
  const handleSelect = (sex: BiologicalSex) => {
    onClickSound?.();
    onSelect(sex);
  };
  return (
    <motion.div
      className="h-full w-full flex items-center justify-center bg-army-black tactical-grid"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <HUDFrame className="w-full max-w-5xl 4k:max-w-[180rem] mx-auto p-6 md:p-8 lg:p-12 4k:p-32">
        <div className="text-center space-y-6 md:space-y-7 lg:space-y-8 4k:space-y-32">
          {/* Back Button */}
          {onBack && (
            <motion.button
              onClick={onBack}
              className="absolute left-6 top-20 4k:top-40 flex items-center space-x-2 4k:space-x-4 text-army-gold hover:text-white transition-colors group z-10"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              whileHover={{ x: -5 }}
            >
              <ChevronLeft className="w-6 h-6 4k:w-16 4k:h-16" />
              <span className="text-sm 4k:text-3xl uppercase tracking-wider font-bold">Back</span>
            </motion.button>
          )}

          {/* Army Logo */}
          <motion.div
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.6 }}
          >
            <img src={ArmyLogo} alt="U.S. Army" className="h-16 md:h-18 lg:h-20 4k:h-64 w-auto mx-auto" />
          </motion.div>

          {/* Header */}
          <motion.div
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <h2 className="text-lg md:text-xl lg:text-2xl 4k:text-8xl text-army-gold uppercase tracking-widest mb-2 4k:mb-12">
              Biometric Intake
            </h2>
            <h1 className="text-3xl md:text-4xl lg:text-5xl 4k:text-11xl font-bold text-white 4k:leading-none">
              INITIALIZING PROFILE
            </h1>
          </motion.div>

          {/* Progress indicator */}
          <motion.div
            className="flex items-center justify-center space-x-2 4k:space-x-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div className="w-10 md:w-11 lg:w-12 4k:w-48 h-1 4k:h-4 bg-army-gold" />
            <div className="w-10 md:w-11 lg:w-12 4k:w-48 h-1 4k:h-4 bg-army-gold" />
            <div className="w-10 md:w-11 lg:w-12 4k:w-48 h-1 4k:h-4 bg-gray-600" />
            <div className="w-10 md:w-11 lg:w-12 4k:w-48 h-1 4k:h-4 bg-gray-600" />
            <div className="w-10 md:w-11 lg:w-12 4k:w-48 h-1 4k:h-4 bg-gray-600" />
          </motion.div>

          {/* Instructions */}
          <motion.p
            className="text-base md:text-lg lg:text-xl 4k:text-7xl text-gray-400 px-4 md:px-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            Select gender profile for accurate uniform generation
          </motion.p>

          {/* Selection Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-7 lg:gap-8 4k:gap-32 mt-8 md:mt-10 lg:mt-12 4k:mt-24">
            {/* Male Option */}
            <motion.button
              onClick={() => handleSelect('male')}
              className="group relative"
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="relative bg-gradient-to-b from-army-dark to-army-black border-4 4k:border-12 border-army-gold px-12 md:px-14 lg:px-16 4k:px-48 py-8 md:py-9 lg:py-10 4k:py-32 card-hover">
                {/* Label */}
                <h3 className="text-3xl md:text-4xl lg:text-4xl 4k:text-[10rem] font-bold text-white uppercase leading-none">
                  Male
                </h3>

                {/* Hover indicator */}
                <motion.div
                  className="absolute inset-0 border-4 4k:border-8 border-white opacity-0 group-hover:opacity-30"
                  initial={false}
                />

                {/* Corner accents */}
                <div className="absolute top-0 left-0 w-8 h-8 4k:w-24 4k:h-24 border-t-4 border-l-4 4k:border-t-12 4k:border-l-12 border-army-gold" />
                <div className="absolute top-0 right-0 w-8 h-8 4k:w-24 4k:h-24 border-t-4 border-r-4 4k:border-t-12 4k:border-r-12 border-army-gold" />
                <div className="absolute bottom-0 left-0 w-8 h-8 4k:w-24 4k:h-24 border-b-4 border-l-4 4k:border-b-12 4k:border-l-12 border-army-gold" />
                <div className="absolute bottom-0 right-0 w-8 h-8 4k:w-24 4k:h-24 border-b-4 border-r-4 4k:border-b-12 4k:border-r-12 border-army-gold" />
              </div>
            </motion.button>

            {/* Female Option */}
            <motion.button
              onClick={() => handleSelect('female')}
              className="group relative"
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="relative bg-gradient-to-b from-army-dark to-army-black border-4 4k:border-12 border-army-gold px-12 md:px-14 lg:px-16 4k:px-48 py-8 md:py-9 lg:py-10 4k:py-32 card-hover">
                {/* Label */}
                <h3 className="text-3xl md:text-4xl lg:text-4xl 4k:text-[10rem] font-bold text-white uppercase leading-none">
                  Female
                </h3>

                {/* Hover indicator */}
                <motion.div
                  className="absolute inset-0 border-4 4k:border-8 border-white opacity-0 group-hover:opacity-30"
                  initial={false}
                />

                {/* Corner accents */}
                <div className="absolute top-0 left-0 w-8 h-8 4k:w-24 4k:h-24 border-t-4 border-l-4 4k:border-t-12 4k:border-l-12 border-army-gold" />
                <div className="absolute top-0 right-0 w-8 h-8 4k:w-24 4k:h-24 border-t-4 border-r-4 4k:border-t-12 4k:border-r-12 border-army-gold" />
                <div className="absolute bottom-0 left-0 w-8 h-8 4k:w-24 4k:h-24 border-b-4 border-l-4 4k:border-b-12 4k:border-l-12 border-army-gold" />
                <div className="absolute bottom-0 right-0 w-8 h-8 4k:w-24 4k:h-24 border-b-4 border-r-4 4k:border-b-12 4k:border-r-12 border-army-gold" />
              </div>
            </motion.button>
          </div>

          {/* Bottom Status */}
          <motion.div
            className="pt-8 4k:pt-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            transition={{ delay: 1 }}
          >
            <p className="text-sm md:text-sm lg:text-base 4k:text-3xl text-gray-500 uppercase tracking-widest">
              Step 2 of 5 • Biometric Configuration
            </p>
          </motion.div>
        </div>
      </HUDFrame>
    </motion.div>
  );
};

export default BiodataEntry;